/**
 * ApprovalCard — presentational human-handoff gate widget.
 *
 * Warning-toned card with a gate icon header ("Human approval required"),
 * body text, optional expiry label, and an actions slot.
 *
 * This is a pure presentational primitive. Each consumer (conversation
 * messages, task detail) wires its own Approve/Reject/Edit buttons and
 * real data into the `actions` slot.
 *
 * NOTE: a task-specific ApprovalCard exists at pages/tasks/ApprovalCard.tsx
 * — that file is data-aware and owns task decision hydration. This component
 * is the generic ui primitive for reuse across contexts.
 *
 * Comp reference: .approval-card (lines 441–444, Confluent Screens.html)
 *   border: 1px solid rgba(250,204,21,0.3); background: var(--warning-muted)
 *   .ah: warning-text, gate icon
 *   .ab: body text
 *   .acts: actions row
 */
import React from 'react';
import { ShieldCheck } from 'lucide-react';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ApprovalCardProps {
  /** Header text — defaults to "Human approval required" */
  title?: React.ReactNode;
  /** Body / description of the action requiring approval */
  body?: React.ReactNode;
  /** Expiry line shown below body, e.g. "Expires in 47h 12m" */
  expiresLabel?: React.ReactNode;
  /** Approve / Reject / other action buttons — caller owns these */
  actions: React.ReactNode;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ApprovalCard({
  title = 'Human approval required',
  body,
  expiresLabel,
  actions,
  className = '',
}: ApprovalCardProps) {
  return (
    <div
      className={['rounded-xl border border-warning/30 bg-warning-muted p-[13px_15px]', className]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Header */}
      <div className="flex items-center gap-2 text-[12.5px] font-semibold text-warning">
        <ShieldCheck size={14} className="shrink-0" />
        {title}
      </div>

      {/* Body */}
      {body && <div className="text-[12.5px] text-body leading-relaxed mt-2 mb-3">{body}</div>}

      {/* Expiry */}
      {expiresLabel && <div className="font-mono text-[10.5px] text-warning mb-3">{expiresLabel}</div>}

      {/* Actions slot */}
      <div className="flex gap-2 flex-wrap">{actions}</div>
    </div>
  );
}
